/* Pocket Factory · conversion layer v1 (pfx).
   Sits on top of pocket.js + pocket-reviews-v2.js: it reads their state and drives their own controls.
   - Button wording always matches the next step ("Preview my cards" -> "Buy 2 cards · $29").
   - Live order summary in the buy bar and the in-flow "ready" block.
   - Pack cards (1 · 2 · 5 + more), progressive disclosure, and a short pause so the reveal is seen. */
(() => {
  'use strict';
  const doc = document, html = doc.documentElement, win = window;
  const product = doc.querySelector('[data-pocket-product]');
  const form = product?.querySelector('form.product-form');
  const store = win.PocketPersonalization?.store;
  if (!form || !store || form.dataset.pfxReady) { html.classList.add('pfx-bar-live'); return; }
  form.dataset.pfxReady = 'true';

  const bar = form.querySelector('[data-buy-bar]');
  const pay = bar?.querySelector('[data-pay-area]');
  /* Quick pay is Shopify's accelerated checkout; if the store renders none, the bar keeps its own Buy button. */
  const walletPresent = !!pay?.querySelector('.shopify-payment-button, shopify-accelerated-checkout');
  html.classList.toggle('pfx-no-wallet', !walletPresent);
  /* Not while another design is in the cart (pocket.js sets data-others): quick pay would pay for this one alone. */
  const quickPay = () => walletPresent && bar?.dataset.state === 'ready' && !bar.hasAttribute('data-others');
  /* Buy is sending the order to the cart: the bar says so and stays as it is (no quick pay swap, no tuck). */
  const sending = () => bar?.dataset.state === 'sending';
  let tuck = () => {}, watchReady = () => {};
  const packSelect = bar?.querySelector('select[data-pack]') || form.querySelector('select[data-pack]');
  const reduced = () => !!win.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const capture = (name, props = {}) => { try { win.posthog?.capture(name, { flow: html.dataset.pfFlow || 'classic', layer: 'pfx-v1', ...props }); } catch (_) {} };
  let variants = [];
  try { variants = JSON.parse(form.querySelector('[data-variant-map]')?.textContent || '[]') || []; } catch (_) {}
  const packOf = variant => parseInt(variant.option1 || variant.title, 10);
  const finishOf = variant => variant.option2 || String(variant.title).split('/')[1]?.trim() || 'Matte';
  const money = cents => {
    const value = cents / 100;
    return '$' + value.toLocaleString('en-US', Number.isInteger(value) ? {} : { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  const nouns = { Reviews: 'Review', Booking: 'Booking', Contact: 'Contact', Social: 'Social', Website: 'Website' };
  const taps = { Reviews: 'Tap opens your review page', Booking: 'Tap opens your booking page', Contact: 'Tap saves your contact details', Social: 'Tap opens your social profile', Website: 'Tap opens your website' };
  const picked = selector => form.querySelector(selector + ':checked')?.value;
  const setText = (selector, text) => doc.querySelectorAll(selector).forEach(node => { if (node.textContent !== text) node.textContent = text; });

  /* Step 03 · pack cards drive the existing pack select, so pocket.js stays the source of truth. */
  const packButtons = [...form.querySelectorAll('[data-pfx-pack]')];
  const packGroup = form.querySelector('[data-pfx-packs]');
  const moreButton = form.querySelector('[data-pfx-more]');
  const expandPacks = focus => {
    if (!packGroup || packGroup.hasAttribute('data-expanded')) return;
    packGroup.setAttribute('data-expanded', '');
    if (moreButton) { moreButton.setAttribute('aria-expanded', 'true'); moreButton.hidden = true; }
    if (focus) packGroup.querySelector('.pfx-pack--more')?.focus({ preventScroll: true });
  };
  moreButton?.addEventListener('click', () => { expandPacks(true); capture('pfx_more_packs_opened'); });
  packButtons.forEach(button => button.addEventListener('click', () => {
    if (!packSelect || packSelect.value === button.dataset.pfxPack) return;
    packSelect.value = button.dataset.pfxPack;
    packSelect.dispatchEvent(new Event('change', { bubbles: true }));
    capture('pfx_pack_selected', { pack: Number(button.dataset.pfxPack) });
  }));

  /* "Questions?" on the bar's last line (Bryton, Oct 22): pocket:questions first, so the call help can answer with its own
     pop-up (it calls preventDefault); otherwise the help chat opens, and closing it comes back here. Its mail link is
     what works without scripts. */
  bar?.querySelector('[data-pfx-questions]')?.addEventListener('click', event => {
    capture('pfx_questions_clicked', { state: bar.dataset.state || '' });
    if (!doc.dispatchEvent(new CustomEvent('pocket:questions', { cancelable: true, detail: { from: 'bar' } }))) { event.preventDefault(); return; }
    if (typeof win.PocketHelp?.open === 'function') { event.preventDefault(); win.PocketHelp.open(event.currentTarget); }
  });
  // The toll-free number beside it dials (the Text / Call script, where it runs, links that call to this card).
  bar?.querySelector('[data-pfx-tel]')?.addEventListener('click', () => capture('pfx_call_clicked', { state: bar.dataset.state || '' }));

  function sync() {
    const brand = store.getState().brand;
    const pack = Number(packSelect?.value) || 2;
    const finish = picked('[data-card-field="finish"]') || 'Matte';
    const purpose = picked('[name="properties[Card purpose]"]') || 'Reviews';
    const back = picked('[data-card-field="backMode"]') || 'Tap + QR';
    const variant = variants.find(item => packOf(item) === pack && finishOf(item) === finish);
    html.classList.toggle('pfx-has-brand', !!brand);
    if (!sending()) html.classList.toggle('pfx-quickpay', quickPay());
    html.dataset.pfxFinish = finish;
    setText('[data-pfx-summary-title]', [pack, finish, nouns[purpose], pack === 1 ? 'Card' : 'Cards'].filter(Boolean).join(' '));
    setText('[data-pfx-summary-back]', back === 'Tap Only' ? 'Tap only' : 'Tap + QR');
    setText('[data-pfx-summary-dest]', form.querySelector('[data-rv-destination-summary]')?.textContent.trim() || '');
    if (variant) {
      setText('[data-pfx-summary-total]', money(variant.price));
      setText('[data-pfx-summary-each]', pack > 1 ? money(Math.round(variant.price / pack)) + ' / card' : 'Single card');
    }
    /* Label = the real next step: preview first; buy once the order can go to checkout; review & buy while details are still needed;
       "Opening checkout…" while Buy sends the order to the cart. */
    const buyable = !!variant && ['ready', 'saving'].includes(bar?.dataset.state), busy = sending() && 'Opening checkout…';
    setText('[data-pfx-cta]', busy || (!brand ? 'Preview my cards' : !variant ? 'Choose a pack' : buyable ? 'Buy ' + pack + (pack === 1 ? ' card · ' : ' cards · ') + money(variant.price) : 'Review & buy · ' + money(variant.price)));
    setText('[data-pfx-cta-short]', busy || (!brand ? 'Preview my cards' : buyable ? 'Buy ' + pack + (pack === 1 ? ' card' : ' cards') : 'Review & buy'));
    /* In-flow summary: quick pay (in the bar) is the primary way to pay, so this becomes the secondary route. */
    setText('[data-pfx-ready-cta]', busy || (quickPay() ? 'More payment options' : !brand ? 'Preview my cards' : !variant ? 'Choose a pack' : buyable ? 'Buy ' + pack + (pack === 1 ? ' card · ' : ' cards · ') + money(variant.price) : 'Review & buy · ' + money(variant.price)));
    /* A card with no name or website is their design alone ("Artwork only") or their logo, as the hero and question 1 say. */
    setText('[data-pfx-business]', brand ? brand.businessName || brand.domain || (win.PocketPersonalization?.cards?.artOnly?.() ? 'Your design' : 'Your logo') : 'Not added yet');
    setText('[data-pfx-benefit-tap]', taps[purpose] || 'Tap opens your link');
    form.querySelectorAll('[data-pfx-benefit-qr]').forEach(node => { node.hidden = back === 'Tap Only'; });
    packButtons.forEach(button => button.setAttribute('aria-pressed', String(Number(button.dataset.pfxPack) === pack)));
    if (packButtons.some(button => button.classList.contains('pfx-pack--more') && Number(button.dataset.pfxPack) === pack)) expandPacks(false);
  }
  let frame = 0;
  const schedule = () => { if (!frame) frame = win.requestAnimationFrame(() => { frame = 0; sync(); }); };
  form.querySelectorAll('[data-rv-order-summary],[data-rv-destination-summary]').forEach(node => new MutationObserver(schedule).observe(node, { childList: true, characterData: true, subtree: true }));
  form.addEventListener('change', schedule);
  doc.addEventListener('pocket:card-config', schedule);
  if (bar) new MutationObserver(() => { schedule(); win.requestAnimationFrame(() => tuck()); }).observe(bar, { attributes: true, attributeFilter: ['data-state', 'data-others'] });
  store.subscribe(schedule);

  /* Progressive disclosure: first-time visitors start from a complete default, not an open wall of options. */
  const panels = [...form.querySelectorAll('[data-rv-editor-panel]')];
  if (!store.getState().brand && panels.some(panel => !panel.hidden)) {
    panels.forEach(panel => { panel.hidden = true; });
    form.querySelectorAll('[data-rv-edit]').forEach(button => button.setAttribute('aria-expanded', 'false'));
    const refinement = form.querySelector('[data-rv-refinement]');
    if (refinement) refinement.dataset.rvEditing = 'false';
  }

  /* Contact cards: essentials first, everything else behind "Add more details". */
  const contact = form.querySelector('.pf-contact-fields');
  if (contact) {
    const extras = [...contact.querySelectorAll('.pf-contact-photo,.pf-contact-split,[name="properties[Contact birthday]"],[name="properties[Contact position]"],[name="properties[Contact website]"],[name="properties[Contact address]"],[name="properties[Contact notes]"]')];
    extras.forEach(node => node.classList.add('pfx-contact-extra'));
    const toggle = doc.createElement('button');
    toggle.type = 'button';
    toggle.className = 'pfx-contact-more';
    toggle.setAttribute('aria-expanded', 'false');
    toggle.textContent = 'Add more details';
    (contact.querySelector('[name="properties[Contact email]"]') || contact.lastElementChild)?.after(toggle);
    const openContact = focus => {
      contact.classList.add('pfx-contact-open');
      toggle.setAttribute('aria-expanded', 'true');
      toggle.hidden = true;
      if (focus) contact.querySelector('.pf-contact-split input')?.focus();
    };
    toggle.addEventListener('click', () => openContact(true));
    const filled = extras.some(node => node.matches('input,textarea') ? !!node.value.trim() : !!node.querySelector('img') || [...node.querySelectorAll('input:not([type=file])')].some(input => !!input.value.trim()));
    if (filled) openContact(false);
    form.addEventListener('pocket:reveal-control', event => { if (event.detail?.target?.closest?.('.pfx-contact-extra')) openContact(false); });
  }

  /* Brief default: Tap + QR on a first visit. Never overrides a returning visitor's choice. */
  let defaultsApplied = false;
  const applyDefaults = () => {
    if (defaultsApplied) return;
    defaultsApplied = true;
    try {
      if (win.localStorage.getItem('pfx.defaults.v1')) return;
      win.localStorage.setItem('pfx.defaults.v1', '1');
    } catch (_) { return; }
    const qr = form.querySelector('[data-card-field="backMode"][value="Tap + QR"]');
    if (qr && !qr.checked && !store.getState().brand) qr.click();
    schedule();
  };
  doc.addEventListener('DOMContentLoaded', applyDefaults, { once: true });
  win.addEventListener('load', applyDefaults, { once: true });

  /* Let the personalized reveal land in the hero before reviews-v2 scrolls to the tap demo. */
  const demo = doc.querySelector('[data-rv-demo]');
  /* pfx_preview_ready: the card draws this visitor's identity, however it was made: a website the lookup read, their
     uploaded logo, their name alone (Oct 22: it counted websites only, so upload and name-only previews never showed in
     the funnel). Once per business; a card still not drawn after 12 s sends pfx_preview_stalled instead. */
  const identity = () => win.PocketPersonalization?.cards?.getIdentity?.() || null;
  const businessOf = brand => brand ? (brand.domain ? 'site:' + brand.domain : 'own') : null;
  const sourceOf = (brand, layers) => {
    if (brand?.domain) return 'website';
    const logo = layers.some(layer => layer.kind === 'image' && layer.role !== 'wordmark');
    return logo ? (brand?.businessName ? 'name_logo' : 'logo_only') : 'name_only';
  };
  /* EXP-001 (PostHog flag pf-exp001-preview-buybar): read once per page as soon as PostHog has its flags, on the
     homepage and product pages only (the pages the experiment runs on), never /pages/pricing or the articles. Reading
     it is what sends PostHog's $feature_flag_called, at 0% too (the answer is then false), so every visit there is
     counted, preview or not (Andy, Oct 23). PostHog sends it once per answer per browser, and never from a browser it
     takes for a bot. The arm is kept on <html data-pf-exp001> and as the pf_exp001 super property, so pfx_preview_ready
     and checkout carry it; both arms see this same page for now. */
  const EXP001 = 'pf-exp001-preview-buybar';
  let exp001 = false;
  const exp001Page = () => {
    const root = (doc.body?.dataset.root || '/').replace(/\/+$/, ''), page = win.location.pathname;
    if (!page.startsWith(root)) return false;
    const rest = page.slice(root.length).replace(/\/+$/, '');
    return rest === '' || /^\/products\/[^/]+$/.test(rest);
  };
  const readExp001 = () => {
    if (exp001 || typeof win.posthog?.onFeatureFlags !== 'function' || !exp001Page()) return;
    exp001 = true;
    let read = false;
    try {
      win.posthog.onFeatureFlags(() => {
        if (read) return;
        read = true;
        // PostHog itself, looked up now: as the page loads, window.posthog can still be the snippet's stand-in, which
        // only queues calls and is replaced once the library loads, so a read through it would answer and send nothing.
        const posthog = win.posthog;
        let value = false;
        try { value = posthog.getFeatureFlag(EXP001); } catch (_) {}
        const arm = value === 'control' || value === 'buy_bar' ? value : 'off';
        html.dataset.pfExp001 = arm;
        try { posthog.register({ pf_exp001: arm }); } catch (_) {}
      });
    } catch (_) {}
  };
  readExp001();
  let business = businessOf(store.getState().brand), revealedAt = 0, previewTimer = 0;
  const watchPreview = () => {
    win.clearTimeout(previewTimer);
    const key = business, started = Date.now();
    const check = () => {
      if (business !== key) return;
      const shown = identity();
      if (shown?.ready && shown.layers?.length) { capture('pfx_preview_ready', { source: sourceOf(store.getState().brand, shown.layers), ms: Date.now() - started }); return; }
      if (Date.now() - started >= 12000) { capture('pfx_preview_stalled', { message: shown?.message || '' }); return; }
      previewTimer = win.setTimeout(check, 200);
    };
    check();
  };
  store.subscribe(() => {
    const next = businessOf(store.getState().brand);
    if (next && next !== business) { business = next; if (next !== 'own') revealedAt = Date.now(); watchPreview(); }
    else business = next;
  });
  if (demo) {
    const nativeScroll = demo.scrollIntoView;
    demo.scrollIntoView = function (options) {
      if (Date.now() - revealedAt < 1500 && !reduced()) { win.setTimeout(() => nativeScroll.call(demo, options), 1100); return; }
      nativeScroll.call(demo, options);
    };
  }

  /* CTA semantics: before a preview exists the action is "Preview my cards"; if the hero field is on screen, go there. */
  const heroInput = doc.getElementById('ReviewsWebsite');
  const onScreen = node => {
    if (!node || node.closest('[hidden]')) return false;
    const rect = node.getBoundingClientRect(), limit = Math.min(win.innerHeight, bar?.getBoundingClientRect().top || win.innerHeight);
    return rect.height > 0 && rect.top >= 0 && rect.bottom <= limit;
  };
  doc.addEventListener('click', event => {
    const cta = event.target.closest?.('[data-buy-bar] [data-purchase],[data-pfx-buy]');
    if (!cta || !form.contains(cta)) return;
    if (store.getState().brand) { capture('pfx_buy_clicked', { from: cta.hasAttribute('data-pfx-buy') ? 'summary' : 'bar', quickpay_shown: quickPay() }); return; }
    if (cta.hasAttribute('data-purchase') && onScreen(heroInput)) {
      event.preventDefault();
      event.stopPropagation();
      heroInput.focus();
      const entry = heroInput.closest('.pfh-entry');
      if (entry) { entry.classList.remove('pfx-nudge'); void entry.offsetWidth; entry.classList.add('pfx-nudge'); }
      capture('pfx_preview_clicked', { route: 'hero' });
      return;
    }
    capture('pfx_preview_clicked', { route: 'dialog' });
  }, true);
  form.querySelector('[data-pfx-buy]')?.addEventListener('click', () => doc.dispatchEvent(new CustomEvent('pocket:purchase')));

  /* One floating action, only when it helps: the bar steps aside while the hero website field is on screen
     (before a preview exists) and while a Buy button of the page is visible above the bar's lower part: the order
     summary's own button, and the product page's Buy and Add to cart. The watched area ends at the lower edge of the
     bar's offer strip (its hide button included), so the strip never sits over one of them (#62). */
  const readyCtas = [...form.querySelectorAll('[data-pfx-buy], .quantity-section > [data-purchase], .quantity-section > [data-add]')];
  // How far down from the bar's top its offer strip reaches (0 without it).
  const band = () => Math.max(0, ...[...bar.querySelectorAll('[data-pfx-offer], [data-pfx-offer-hide]')].filter(node => node.offsetHeight).map(node => node.offsetTop + node.offsetHeight));
  const heroEntry = doc.querySelector('.pf-rv-hero.pfh') || doc.querySelector('.pf-rv-hero .pfh-action');
  const inView = (node, bottomInset = 0) => { if (!node) return false; const r = node.getBoundingClientRect(); return r.height > 0 && r.bottom > 0 && r.top < win.innerHeight - bottomInset; };
  let heroInView = inView(heroEntry), readyInView = false, readyObserver = null;
  const readyShown = new Set();
  /* Never tuck while quick pay is showing: it is the fastest way to pay and must stay on screen. While Buy is sending,
     the bar stays where it is. */
  tuck = () => { if (!sending()) html.classList.toggle('pfx-bar-tucked', !quickPay() && ((heroInView && !store.getState().brand) || readyInView)); };
  tuck();
  if (bar && typeof win.IntersectionObserver === 'function') {
    if (heroEntry) new win.IntersectionObserver(entries => entries.forEach(entry => { heroInView = entry.isIntersecting; tuck(); }), { rootMargin: '0px 0px -35% 0px' }).observe(heroEntry);
    watchReady = () => {
      readyObserver?.disconnect(); readyShown.clear(); readyInView = false;
      if (!readyCtas.length) return;
      // The bar's resting place (offsetTop has no slide-in transform); before it is laid out, its height plus 8px.
      const inset = Math.max(0, Math.ceil(bar.offsetHeight ? win.innerHeight - bar.offsetTop - band() : bar.getBoundingClientRect().height + 8));
      readyObserver = new win.IntersectionObserver(entries => {
        entries.forEach(entry => { if (entry.isIntersecting) readyShown.add(entry.target); else readyShown.delete(entry.target); });
        readyInView = readyShown.size > 0; tuck();
      }, { rootMargin: '0px 0px -' + inset + 'px 0px' });
      readyCtas.forEach(node => readyObserver.observe(node));
    };
    watchReady();
    let resizeTimer = 0;
    win.addEventListener('resize', () => { win.clearTimeout(resizeTimer); resizeTimer = win.setTimeout(watchReady, 200); }, { passive: true });
    store.subscribe(tuck);
  }
  win.requestAnimationFrame(() => win.requestAnimationFrame(() => html.classList.add('pfx-bar-live')));

  /* Hero: roomy while the buy bar is out of the way. Once the product is enabled the bar stays on screen,
     so the hero tightens to sit above it. Off screen it snaps and the scroll position is compensated. */
  const hero = doc.querySelector('.pf-rv-hero.pfh');
  if (hero) {
    let compact = null, settle = 0;
    const applyHero = () => {
      const want = !!store.getState().brand;
      if (want === compact) return;
      if (compact === null) {
        /* First paint: take the right size without animating (returning visitors already have a preview). */
        html.classList.add('pfx-hero-instant');
        html.classList.toggle('pfx-hero-compact', want);
        compact = want;
        win.requestAnimationFrame(() => win.requestAnimationFrame(() => html.classList.remove('pfx-hero-instant')));
        return;
      }
      if (hero.getBoundingClientRect().bottom <= 0) {
        if (settle) return;
        const before = hero.offsetHeight;
        html.classList.add('pfx-hero-instant');
        html.style.overflowAnchor = 'none';
        html.classList.toggle('pfx-hero-compact', want);
        const delta = hero.offsetHeight - before;
        if (delta) win.scrollBy(0, delta);
        win.requestAnimationFrame(() => { html.classList.remove('pfx-hero-instant'); html.style.overflowAnchor = ''; });
      } else if (want) return; // On screen it waits (the scroll handler retries): tightening there shifts what the shopper is looking at.
      else html.classList.toggle('pfx-hero-compact', want);
      compact = want;
    };
    win.addEventListener('scroll', () => { win.clearTimeout(settle); settle = win.setTimeout(() => { settle = 0; applyHero(); }, 160); }, { passive: true });
    store.subscribe(applyHero);
    applyHero();
  }

  /* Hero v2: example sites rotate in the empty field, the cards tilt toward the pointer on desktop,
     a new brand flips the cards in, and idle motion pauses while the hero is off screen. */
  if (hero) {
    const heroForm = hero.querySelector('.pfh-form');
    const heroField = heroForm?.querySelector('input');
    const example = heroForm?.querySelector('[data-pfh-ph]');
    let heroVisible = true;
    if (typeof win.IntersectionObserver === 'function') {
      new win.IntersectionObserver(entries => entries.forEach(entry => { heroVisible = entry.isIntersecting; hero.dataset.pfhIdle = heroVisible ? 'on' : 'off'; })).observe(hero);
    }
    if (heroForm && heroField && example) {
      // After the field's own example: 14 characters or fewer each, so every one fits the field on a 320px phone.
      const sites = ['joescuts.com', 'harborcafe.co', 'glowspa.com', 'oakdental.com', 'lenastudio.com'];
      let site = -1;
      const empty = () => !heroField.value && doc.activeElement !== heroField;
      const syncExample = () => heroForm.classList.toggle('pfh-ph-on', empty());
      ['focus', 'blur', 'input'].forEach(type => heroField.addEventListener(type, syncExample));
      syncExample();
      if (!reduced()) {
        win.setInterval(() => {
          if (!heroVisible || doc.hidden || !empty()) return;
          site = (site + 1) % sites.length;
          example.classList.add('is-out');
          win.setTimeout(() => {
            example.textContent = sites[site];
            example.classList.remove('is-out');
            example.classList.add('is-in');
            void example.offsetWidth;
            example.classList.remove('is-in');
          }, 280);
        }, 2600);
      }
    }
    const stage = hero.querySelector('[data-pfh-stage]');
    if (stage && win.matchMedia?.('(hover: hover) and (pointer: fine)').matches && !reduced()) {
      let tiltFrame = 0, px = 0, py = 0;
      hero.addEventListener('pointermove', event => {
        const box = stage.getBoundingClientRect();
        px = Math.max(-1, Math.min(1, (event.clientX - (box.left + box.width / 2)) / (box.width * 0.9)));
        py = Math.max(-1, Math.min(1, (event.clientY - (box.top + box.height / 2)) / (box.height * 1.2)));
        if (!tiltFrame) tiltFrame = win.requestAnimationFrame(() => {
          tiltFrame = 0;
          stage.style.setProperty('--pfh-tx', (px * 7).toFixed(2) + 'deg');
          stage.style.setProperty('--pfh-ty', (-py * 5).toFixed(2) + 'deg');
        });
      }, { passive: true });
      hero.addEventListener('pointerleave', () => { stage.style.setProperty('--pfh-tx', '0deg'); stage.style.setProperty('--pfh-ty', '0deg'); });
    }
    let shownDomain = store.getState().brand?.domain || null, revealTimer = 0;
    store.subscribe(() => {
      const next = store.getState().brand?.domain || null;
      if (next && next !== shownDomain && !reduced()) {
        hero.classList.remove('pfh-revealing');
        void hero.offsetWidth;
        hero.classList.add('pfh-revealing');
        win.clearTimeout(revealTimer);
        revealTimer = win.setTimeout(() => hero.classList.remove('pfh-revealing'), 1300);
      }
      shownDomain = next;
    });
  }

  /* Tap demo: the same card, tapped by one phone after another, looping while it is on screen. */
  const demoPhone = demo?.querySelector('[data-rv-phone]');
  if (demoPhone) {
    const posted = demo.querySelector('[data-pfx-posted]');
    const postedText = { Reviews: 'Review posted', Booking: 'Booking page opened', Contact: 'Contact saved', Social: 'Profile opened', Website: 'Website opened' };
    const providerLogo = demo.querySelector('[data-rv-provider-logo]');
    const syncPhone = () => {
      if (posted) posted.textContent = postedText[demoPhone.dataset.purpose] || 'Page opened';
      demoPhone.dataset.pfxProvider = providerLogo && !providerLogo.hidden ? (providerLogo.getAttribute('alt') || 'other').toLowerCase() : 'other';
    };
    new MutationObserver(syncPhone).observe(demoPhone, { attributes: true, attributeFilter: ['data-purpose'] });
    if (providerLogo) new MutationObserver(syncPhone).observe(providerLogo, { attributes: true, attributeFilter: ['alt', 'hidden', 'src'] });
    syncPhone();
    if (typeof win.IntersectionObserver === 'function' && !reduced()) {
      demo.dataset.pfxLoop = 'on';
      const tag = demo.querySelector('.pfx-tap-tag'), count = demo.querySelector('[data-pfx-tap-n]');
      let taps = 0, cycles = 0;
      const tapped = event => {
        if (event.target !== tag) return;
        taps += 1;
        if (count) count.textContent = String(taps);
        tag.dataset.count = String(taps);
      };
      tag?.addEventListener('animationstart', tapped);
      tag?.addEventListener('animationiteration', tapped);
      demoPhone.addEventListener('animationiteration', event => {
        if (event.target !== demoPhone) return;
        cycles += 1;
        demoPhone.dataset.pfxSkin = String(cycles % 3);
        if (cycles === 1) capture('pf_tap_demo_completed', { motion: 'loop' });
      });
      /* The old one-shot "Replay" control becomes pause / play for the loop. */
      const control = demo.querySelector('[data-rv-replay]');
      if (control) { control.textContent = 'Pause demo'; control.setAttribute('aria-pressed', 'false'); }
      /* "Watch a tap" ([data-pfq-go], homepage questions) replays the demo by clicking this control. That click must
         bring the demo into view and play it from the start, never toggle it to paused. */
      let fromGo = false;
      doc.addEventListener('click', event => {
        if (!event.target.closest?.('[data-pfq-go]')) return;
        fromGo = true;
        win.setTimeout(() => { fromGo = false; });
      }, true);
      doc.addEventListener('click', event => {
        const button = event.target.closest?.('[data-rv-replay]');
        if (!button || !demo.contains(button)) return;
        // Reduced motion: reviews-v2's own replay (instant scroll, the static end state) handles it.
        if (fromGo && reduced()) { fromGo = false; return; }
        event.preventDefault();
        event.stopImmediatePropagation();
        if (fromGo) {
          fromGo = false;
          demo.removeAttribute('data-pfx-paused');
          button.textContent = 'Pause demo';
          button.setAttribute('aria-pressed', 'false');
          delete demo.dataset.pfxLoop; void demo.offsetWidth; demo.dataset.pfxLoop = 'on'; // restart from the first frame
          demo.scrollIntoView({ behavior: 'smooth', block: 'start' });
          if (demo.hasAttribute('tabindex')) demo.focus({ preventScroll: true });
          return;
        }
        const paused = demo.toggleAttribute('data-pfx-paused');
        button.textContent = paused ? 'Play demo' : 'Pause demo';
        button.setAttribute('aria-pressed', String(paused));
      }, true);
      win.matchMedia?.('(prefers-reduced-motion: reduce)').addEventListener?.('change', event => { if (event.matches) delete demo.dataset.pfxLoop; });
    }
  }

  /* Measure quick pay usage (Shopify exposes these wallet elements for click tracking). */
  const wallets = /^(SHOPIFY-APPLE-PAY-BUTTON|SHOP-PAY-WALLET-BUTTON|SHOPIFY-GOOGLE-PAY-BUTTON|SHOPIFY-PAYPAL-BUTTON|SHOPIFY-AMAZON-PAY-BUTTON|SHOPIFY-BUY-IT-NOW-BUTTON)$/;
  doc.addEventListener('click', event => {
    const hit = (event.composedPath?.() || []).find(node => node?.tagName && wallets.test(node.tagName));
    if (hit) capture('pfx_quickpay_clicked', { wallet: hit.tagName.toLowerCase() });
  }, true);

  /* Focus is never left behind the fixed bar (its offer strip is docked inside it): a control or heading focused there
     (for example with preventScroll) is scrolled just clear of it; scroll-padding-bottom follows the bar. A homepage
     question heading is left where it is: after Preview the card above it is what the shopper needs to see. */
  // The bar is away before it goes live, while tucked and while the keyboard is open.
  const barAway = () => (html.dataset.pfFlow === 'reviews-v2' && !html.classList.contains('pfx-bar-live')) || html.classList.contains('pfx-bar-tucked') || html.classList.contains('pf-keyboard-open');
  const keepClear = target => {
    if (!bar || !target || target === doc.body || !target.getBoundingClientRect || bar.contains(target) || target.matches?.('.pfq-title')) return;
    // Its resting place is used (offsetTop, without the slide-in transform).
    if (barAway() || win.getComputedStyle(bar).display === 'none') return;
    // A visually hidden radio or checkbox is shown by its label: keep the whole label clear.
    const shown = target.matches?.('input[type=radio],input[type=checkbox]') ? target.closest('label') || target : target;
    const cover = bar.offsetTop, box = shown.getBoundingClientRect();
    // Things that fit above the bar only (a section focused while it scrolls into view is left alone), partly or
    // fully under the bar.
    if (box.height > 0 && box.height < cover - 8 && box.bottom > cover && box.top < win.innerHeight) shown.scrollIntoView({ block: 'nearest' });
  };
  if (bar) {
    let focused = null, focusedAt = 0, away = barAway();
    doc.addEventListener('focusin', event => {
      const target = event.target; focused = target; focusedAt = Date.now();
      win.requestAnimationFrame(() => { if (doc.activeElement === target) keepClear(target); });
    });
    // The bar may arrive just after the focus did (it goes live once it is the most useful action on screen). Only a
    // focus from the last few seconds is brought back, so scrolling away from a focused control is never undone.
    new win.MutationObserver(() => {
      const now = barAway(), arrived = away && !now; away = now;
      if (arrived && focused === doc.activeElement && Date.now() - focusedAt < 4000) win.requestAnimationFrame(() => keepClear(focused));
    }).observe(html, { attributes: true, attributeFilter: ['class'] });
  }

  /* Small persistent offer tab; the full offer only opens on request or after real engagement. */
  const tab = bar?.querySelector('[data-pfx-offer]');
  if (tab) {
    const hide = bar.querySelector('[data-pfx-offer-hide]'), again = form.querySelector('[data-pfx-offer-again]');
    const refreshTab = () => {
      const off = !win.PocketEmailOffer?.available?.(); tab.hidden = off; bar.classList.toggle('pfx-offer-off', off);
      if (hide) hide.hidden = off;
      if (again) again.parentElement.hidden = off;
      watchReady();
    };
    tab.addEventListener('click', () => { if (win.PocketEmailOffer?.open('offer_tab')) capture('pfx_offer_tab_opened'); });
    // #62: the way back to the offer once the strip is hidden (it is in the page whenever the offer is available).
    again?.addEventListener('click', () => { if (win.PocketEmailOffer?.open('offer_again')) capture('pfx_offer_again_opened'); });
    // #62: hide the docked strip for the rest of the session. The bar is anchored at the bottom, so its own buttons do
    // not move; pocket.js holds --buy-height at the current height for this page view (data-reserve), so the page
    // (pinned preview, hero, scroll padding) does not move either. Focus goes to the bar's main button.
    hide?.addEventListener('click', () => {
      bar.dataset.reserve = String(Math.ceil(bar.getBoundingClientRect().height));
      html.classList.add('pfx-offer-hidden');
      try { win.sessionStorage.setItem('pf.offerStrip.hidden', '1'); } catch (_) {}
      const main = [...bar.querySelectorAll('[data-pf-buy-now], [data-purchase], [data-pay-area] button')].find(node => !node.closest('[hidden], [inert]'));
      main?.focus({ preventScroll: true });
      capture('pfx_offer_strip_hidden');
      watchReady();
    });
    // Docked inside the bar on phones, the tab's strip is reserved from the first paint (CSS) and only collapses once the
    // offer is known to be unavailable (pfx-offer-off), so the tab appearing never moves the bar (layout shift).
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', refreshTab, { once: true });
    win.addEventListener('load', refreshTab, { once: true });
    doc.getElementById('PFEmailOffer')?.addEventListener('close', refreshTab);
  }

  sync();
})();
